import { LOCALES } from '@/enums/locale'
import { TENANT_ROLES, TenantRole } from '@/enums/tenant'
import { Errors, errorCode, parseKnownError } from '@/libs/error'
import i18n from '@/libs/i18n'
import { useFetchStore } from '@/stores/fetch'
import type { SelectOption, TagItem } from '@/types/Common'
import type { IMemberAddRequest, IMembership, IUser } from '@/types/User'

/** A tenant role's name: Skrbnik, Urednik (docs/API.md → UI labels). */
export const roleLabel = (role: string | null | undefined): string => {
    switch (role) {
        case TenantRole.Admin:  return i18n.global.t('role.admin')
        case TenantRole.Editor: return i18n.global.t('role.editor')
        case TenantRole.Reception: return i18n.global.t('role.reception')
        case TenantRole.Kitchen:   return i18n.global.t('role.kitchen')
        case TenantRole.Housekeeping: return i18n.global.t('role.housekeeping')
        case TenantRole.Technician: return i18n.global.t('role.technician')
        default:                return role ?? ''
    }
}

export const roleOptions = (): SelectOption[] => TENANT_ROLES.map((role) => ({ label: roleLabel(role), value: role }))

/*
 * The badge of an account's access: a provider admin carries the accent (the account that
 * sees every tenant), a tenant admin the information colour, an editor stays neutral.
 */
export const accessTag = (providerAdmin: boolean, role: string | null): TagItem => {
    if (providerAdmin)            return { label: i18n.global.t('role.provider_admin'), color: 'primary', icon: 'i-lucide-shield-check' }
    if (role === TenantRole.Admin) return { label: roleLabel(role), color: 'info' }

    return { label: roleLabel(role), color: 'neutral' }
}

/*
 * One membership as a small badge: "Hotel Lipa · Skrbnik" on the provider's list, just "Hotel
 * Lipa" where the role is shown elsewhere (the other tenants on a tenant's list).
 */
export const membershipTag = (membership: IMembership, withRole = true): TagItem => ({
    label: withRole ? `${membership.tenant_name} · ${roleLabel(membership.role)}` : membership.tenant_name,
    color: withRole && membership.role === TenantRole.Admin ? 'info' : 'neutral',
})

/*
 * POST /tenants/{tid}/members — an EXISTING account (its exact username) joins the tenant with a
 * role. `error` is the sentence to show when it did not: no such active account (404 code 3),
 * already a member (409 code 20), or whatever else the API said.
 */
export const addMember = async (tid: string, body: IMemberAddRequest): Promise<{ ok: boolean, error: string }> => {
    const { statusCode, data } = await useFetchStore().fetch(`/tenants/${tid}/members`).post(body).json<IUser>()

    if (statusCode.value === 200) return { ok: true, error: '' }

    const error = errorCode(data.value) === Errors.NotFound
        ? i18n.global.t('user.member_not_found')
        : parseKnownError(data.value, i18n.global.t('errors.on_add'))

    return { ok: false, error }
}

/** "Ana Novak" → "AN"; a username alone ("ana.novak") gives "AN" too. */
export const initials = (name: string): string => name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

/** Usernames: 3–32 characters of a–z, 0–9, dot, underscore and dash. */
export const USERNAME_PATTERN = /^[a-z0-9._-]{3,32}$/

export const MIN_PASSWORD = 8

/*
 * The two languages, each written in itself ("Slovenščina", "English"), so a person who
 * cannot read the current one still finds their own.
 */
export const localeOptions = (): SelectOption[] =>
    LOCALES.map((code) => ({ label: i18n.global.t(`locale.${code}`), value: code }))
